import { createAuthorizedApiCall } from 'infrastructure/t4/api/authorizedApiCall';
import { ApiError, createApiCall } from 'infrastructure/t4/api/apiCall';
import * as Auth from 'infrastructure/t4/api/Authorization/apiCall';
import * as VehicleRegistry from 'infrastructure/t4/api/VehicleRegistry';
import * as MediaDevice from 'infrastructure/t4/api/MediaDevice';
import * as Role from 'infrastructure/t4/api/Role';
import * as Control from 'infrastructure/t4/api/Control';
import * as FMS from 'infrastructure/t4/api/FMS';
import { ServerConfigParams, AuthConfigParams } from 'config';
import { createTokenKeeper as createAuthTokenKeeper } from 'infrastructure/t4/api/TokenKeeper';
import { createTokenKeeper as createOIDCTokenKeeper } from 'infrastructure/oidc/TokenKeeper';
import { OIDCClient } from 'infrastructure/oidc/OIDCClient';
import { connectAuthorizedStream } from 'infrastructure/t4/api/authorizedStream';
import { MediaClient } from '@tier4/webauto-media-client';
import { createStreamConnectorWithErrorHook, createApiCallWithErrorHook } from 'infrastructure/t4/api/errorHook';

export function createConfiguredApiCalls(serverConfig: Partial<ServerConfigParams>, authConfig: Partial<AuthConfigParams>, oidcClient: OIDCClient | undefined, onMaintenance: (e: ApiError) => void) {
    const onError = (e: unknown) => {
        if (e instanceof ApiError) {
            if (e.status === 503) {
                onMaintenance(e);
            }
        }
    };

    const directApiCall = createApiCall();

    const authApiCall = serverConfig.auth
        ? Auth.createApiCall(directApiCall, serverConfig.auth.baseUrl)
        : undefined;

    const authTokenGenerator = oidcClient && authApiCall
        ? createAuthTokenKeeper(createOIDCTokenKeeper(oidcClient), authApiCall, authConfig.grantType)
        : undefined;

    const vehicleApiCall = serverConfig.vehicle
        ? VehicleRegistry.createApiCall(directApiCall, serverConfig.vehicle.baseUrl)
        : undefined;

    const authorizedVehicleApiCall = vehicleApiCall && authTokenGenerator
        ? createAuthorizedApiCall(authTokenGenerator, vehicleApiCall)
        : vehicleApiCall;

    const mediaDeviceApiCall = serverConfig.monitor
        ? MediaDevice.createApiCall(directApiCall, serverConfig.monitor.baseUrl)
        : undefined;
    const authorizedMediaDeviceApiCall = mediaDeviceApiCall && authTokenGenerator
        ? createAuthorizedApiCall(authTokenGenerator, mediaDeviceApiCall)
        : undefined;

    const mediaDeviceStream = serverConfig.monitor
        ? MediaDevice.createStream(serverConfig.monitor.streamBaseUrl)
        : undefined;
    const authorizedMediaDeviceStream = mediaDeviceStream && authTokenGenerator
        ? connectAuthorizedStream(authTokenGenerator, mediaDeviceStream)
        : mediaDeviceStream;

    const controlApiCall = serverConfig.control
        ? Control.createApiCall(directApiCall, serverConfig.control.baseUrl)
        : undefined;
    const authorizedControlApiCall = controlApiCall && authTokenGenerator
        ? createAuthorizedApiCall(authTokenGenerator, controlApiCall)
        : undefined;

    const controlStream = serverConfig.control
        ? createStreamConnectorWithErrorHook(Control.createStream(serverConfig.control.streamBaseUrl), onError)
        : undefined;
    const authorizedControlStream = controlStream && authTokenGenerator
        ? connectAuthorizedStream(authTokenGenerator, controlStream)
        : controlStream;

    const roleApiCall = serverConfig.role
        ? Role.createApiCall(directApiCall, serverConfig.role.baseUrl)
        : undefined;
    const authorizedRoleApiCall = roleApiCall && authTokenGenerator
        ? createApiCallWithErrorHook(createAuthorizedApiCall(authTokenGenerator, roleApiCall), onError)
        : roleApiCall;

    const fmsApiCall = serverConfig.fms
        ? createApiCallWithErrorHook(FMS.createApiCall(directApiCall, serverConfig.fms.baseUrl), onError)
        : undefined;

    const fmsStream = serverConfig.fms
        ? FMS.createStream(serverConfig.fms.streamBaseUrl)
        : undefined;

    const authorizedFmsApiCall = fmsApiCall && authTokenGenerator
        ? createAuthorizedApiCall(authTokenGenerator, fmsApiCall)
        : fmsApiCall;

    if (serverConfig.monitor?.baseUrl) {
        MediaClient.Config.mediaApiBaseUrl = serverConfig.monitor.baseUrl;
    }

    return {
        roleApiCall: authorizedRoleApiCall,
        fmsApiCall: authorizedFmsApiCall,
        fmsStream,
        vehicleApiCall: authorizedVehicleApiCall,
        mediaDeviceApiCall: authorizedMediaDeviceApiCall,
        authApiCall: authApiCall,
        mediaDeviceStream: authorizedMediaDeviceStream,
        controlApiCall: authorizedControlApiCall,
        controlStream: authorizedControlStream,
        revokeToken: authTokenGenerator ? () => authTokenGenerator.revoke() : undefined,
        getToken: authTokenGenerator ? () => authTokenGenerator.get() : undefined,
        authTokenKeeper: authTokenGenerator,
    };
}
